<template>
  <FullPage class="meetings" @contextmenu.prevent>
    <BaseNumberedHeader title="Meetings" :number="totalMeetings" />
    <div class="search-bar">
      <BaseInput
        ref="searchInputRef"
        v-model:value="searchInput"
        placeholder="Search meetings..."
        has-styles
        class="search-input"
      >
        <template #left>
          <SearchIcon class="search-icon" />
        </template>
      </BaseInput>
    </div>
    <Suspense>
      <MeetingsList
        class="list"
        :search-query="searchQuery"
        @update:total="totalMeetings = $event"
      />
      <template #fallback>
        <Loader class="loader">
          <BaseSkeletonList />
        </Loader>
      </template>
    </Suspense>
  </FullPage>
</template>

<script lang="ts">
import { debounce } from "lodash-es";
import { defineComponent, onMounted, ref, watch } from "vue";

import BaseInput from "@/components/base/BaseInput.vue";
import BaseNumberedHeader from "@/components/base/BaseNumberedHeader.vue";
import BaseSkeletonList from "@/components/base/BaseSkeletonList.vue";
import SearchIcon from "@/components/icons/SearchIcon.vue";
import Loader from "@/components/Loader.vue";
import MeetingsList from "@/components/meetings/MeetingsList.vue";
import FullPage from "@/components/templates/FullPage.vue";

export default defineComponent({
  name: "Meetings",
  components: {
    BaseInput,
    BaseNumberedHeader,
    BaseSkeletonList,
    FullPage,
    Loader,
    MeetingsList,
    SearchIcon,
  },
  setup() {
    const searchInput = ref("");
    const searchQuery = ref("");
    const totalMeetings = ref(0);
    const searchInputRef = ref<InstanceType<typeof BaseInput>>();

    // The search is client-side; debounce only enough to avoid regrouping
    // the whole list on every keystroke.
    const debouncedUpdateSearch = debounce((value: string) => {
      searchQuery.value = value;
    }, 200);

    watch(searchInput, (newValue) => {
      debouncedUpdateSearch(newValue);
    });

    onMounted(() => {
      searchInputRef.value?.focus();
    });

    return {
      searchInput,
      searchInputRef,
      searchQuery,
      totalMeetings,
    };
  },
});
</script>

<style scoped lang="scss">
.meetings {
  .search-bar {
    display: flex;
    align-items: center;
    width: 100%;

    .search-input {
      width: 100%;
      @extend %font-body-regular;
    }

    :deep(svg) {
      width: 16px;
      height: 16px;
      margin-top: 4px;
    }

    @include themed() {
      :deep(svg path) {
        stroke: t($grey-40);
      }
    }
  }

  .list {
    overflow-y: scroll;
    height: 100%;
    margin-inline-start: -64px;
    margin-inline-end: -64px;
    margin-bottom: 16px;
  }
}
</style>
